/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useState } from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/lib/isEmail';
import Modal from '../Modal';
import { setNoteIdentity } from '../../utils/noteIdentityService';
import { FIELD_LIMITS } from '../../utils/constants';

// Blocking, non-dismissable prompt for the name/email used to attribute notes.
// Built on the shared Modal with dismissable={false} — no backdrop-click/escape-close.
const NoteIdentityGate = ({ isOpen, token, branding = {}, isOnline = true, onSubmit }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const validateField = (field, value) => {
    const trimmed = value.trim();
    if (field === 'name') {
      if (!trimmed) return 'Please enter your name.';
      if (trimmed.length < FIELD_LIMITS.MIN_LENGTH) return `Name must be at least ${FIELD_LIMITS.MIN_LENGTH} characters long.`;
      if (trimmed.length > FIELD_LIMITS.DEFAULT_TEXT) return `Name cannot exceed ${FIELD_LIMITS.DEFAULT_TEXT} characters.`;
      return null;
    }
    if (field === 'email') {
      if (!trimmed) return 'Please enter your email.';
      if (trimmed.length > FIELD_LIMITS.EMAIL) return `Email cannot exceed ${FIELD_LIMITS.EMAIL} characters.`;
      if (!isEmail(trimmed)) return 'Please enter a valid email address.';
      return null;
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const nameError = validateField('name', name);
    const emailError = validateField('email', email);
    if (nameError) errors.name = nameError;
    if (emailError) errors.email = emailError;
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    const fieldError = validateField(field, value);
    setFieldErrors(prev => {
      const next = { ...prev };
      if (fieldError) next[field] = fieldError;
      else delete next[field];
      return next;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!isOnline) {
      setError('You need an internet connection to proceed with this step.');
      return;
    }
    setError('');

    if (!validateForm()) return;

    setNoteIdentity(token, { name: name.trim(), email: email.trim() });
    onSubmit();
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { }} dismissable={false} theme={branding.theme}>
      <form onSubmit={handleSubmit}>
        <h3 className="text-lg font-bold text-base-content mb-1">Before you continue</h3>
        <p className="text-[13px] text-base-content/70 mb-5">
          Enter your name and email so any notes you add can be attributed to you.
        </p>

        {!isOnline ? (
          <p className="mb-3 text-[13px] text-warning">
            You need an internet connection to proceed with this step. You can disconnect again afterward.
          </p>
        ) : null}

        <label htmlFor='name' className="block text-[13px] font-semibold text-base-content mb-1">Name</label>
        <input
          id='name'
          type="text"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setError('');
            setFieldErrors(prev => {
              if (!prev.name) return prev;
              const next = { ...prev };
              delete next.name;
              return next;
            });
          }}
          onBlur={(event) => handleBlur('name', event.target.value)}
          placeholder="Your name"
          maxLength={FIELD_LIMITS.DEFAULT_TEXT}
          autoFocus
          aria-invalid={!!fieldErrors.name}
          aria-describedby={fieldErrors.name ? 'name-error' : undefined}
          className={`w-full mb-1 h-11 px-3 rounded-xl border bg-base-100 text-base-content text-base outline-none focus:ring-2 focus:ring-primary/20 ${fieldErrors.name ? 'border-error' : 'border-base-300 focus:border-primary'}`}
        />
        {fieldErrors.name ? <p id="name-error" role="alert" className="mb-2 text-[13px] text-error">{fieldErrors.name}</p> : <div className="mb-3" />}

        <label htmlFor='email' className="block text-[13px] font-semibold text-base-content mb-1">Email</label>
        <input
          id='email'
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setError('');
            setFieldErrors(prev => {
              if (!prev.email) return prev;
              const next = { ...prev };
              delete next.email;
              return next;
            });
          }}
          onBlur={(event) => handleBlur('email', event.target.value)}
          placeholder="you@example.com"
          maxLength={FIELD_LIMITS.EMAIL}
          aria-invalid={!!fieldErrors.email}
          aria-describedby={fieldErrors.email ? 'email-error' : undefined}
          className={`w-full mb-1 h-11 px-3 rounded-xl border bg-base-100 text-base-content text-base outline-none focus:ring-2 focus:ring-primary/20 ${fieldErrors.email ? 'border-error' : 'border-base-300 focus:border-primary'}`}
        />
        {fieldErrors.email ? <p id="email-error" role="alert" className="mb-2 text-[13px] text-error">{fieldErrors.email}</p> : <div className="mb-2" />}

        {error ? <p role="alert" className="mb-2 text-[13px] text-error">{error}</p> : null}

        <button
          type="submit"
          className="w-full mt-3 btn btn-primary font-bold text-[16px] rounded-xl shadow-md active:scale-95 transition-all"
        >
          Continue
        </button>
      </form>
    </Modal>
  );
};

export default NoteIdentityGate;

NoteIdentityGate.propTypes = {
  isOpen: PropTypes.bool,
  token: PropTypes.string.isRequired,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  isOnline: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};
